import { ApiService } from '../../services/api';
import {
  ApiMasterLabelBarcodeLookupResponse,
  ApiMasterLabelDesignLookupResponse,
  ApiMasterLabelUPCData,
  DailyReportAggregateSnapshot,
  DailyReportSnapshot,
  DataCollectionCreateRequest,
  DataCollectionCreateResponse,
  DataCollectionCompleteBatchResponse,
  DataCollectionDashboardResponse,
  DataCollectionRequestedType,
  DataCollectionRequestRow,
  DataCollectionStatus,
  DataCollectionTargetScope,
  ForgeDesignGroupSearchResponse,
  LiveStatusSnapshot,
  MasterLabelStateSaveRequest,
  MasterLabelStateSaveResponse,
  MasterLabelUnresolvedChainSnapshot,
  MasterLabelUnresolvedDashboardResponse,
  ModifyForgeDesignGroupsRequest,
  ModifyForgeDesignGroupsResponse,
} from '../types';

const API_DL_URL = process.env.REACT_APP_API_DL_URL;

if (!API_DL_URL) {
  throw new Error('REACT_APP_API_DL_URL is required for Forge API requests');
}

const inFlightDailyReportRequests = new Map<string, Promise<DailyReportSnapshot>>();
const inFlightLiveStatusRequests = new Map<string, Promise<LiveStatusSnapshot>>();
const inFlightMasterLabelUnresolvedRequests = new Map<
  string,
  Promise<MasterLabelUnresolvedDashboardResponse>
>();

export class ForgeApiError extends Error {
  status: number;

  detail?: string;

  rawBody?: string;

  constructor(message: string, status: number, detail?: string, rawBody?: string) {
    super(message);
    this.name = 'ForgeApiError';
    this.status = status;
    this.detail = detail;
    this.rawBody = rawBody;
  }
}

async function parseApiError(response: Response, fallback: string): Promise<ForgeApiError> {
  const rawBody = await response.text().catch(() => '');
  let payload: { detail?: string; message?: string } | null = null;

  if (rawBody) {
    try {
      payload = JSON.parse(rawBody) as { detail?: string; message?: string };
    } catch {
      payload = null;
    }
  }

  const detail = payload?.detail || payload?.message || rawBody || undefined;
  const message = detail || `${fallback} (${response.status} ${response.statusText})`;

  return new ForgeApiError(message, response.status, detail, rawBody || undefined);
}

export class ForgeApiService {
  static async fetchDataCollectionDashboard(
    token: string,
    filters?: {
      status?: DataCollectionStatus | 'all';
      chainId?: number | null;
      targetScope?: DataCollectionTargetScope | 'all' | null;
      requestedType?: DataCollectionRequestedType | 'all' | null;
      groupBy?: 'item_number' | null;
    }
  ): Promise<DataCollectionDashboardResponse> {
    const params = new URLSearchParams();
    if (filters?.status) params.set('status', filters.status);
    if (filters?.chainId) params.set('chain_id', String(filters.chainId));
    if (filters?.targetScope && filters.targetScope !== 'all') {
      params.set('target_scope', filters.targetScope);
    }
    if (filters?.requestedType && filters.requestedType !== 'all') {
      params.set('requested_type', filters.requestedType);
    }
    if (filters?.groupBy) params.set('group_by', filters.groupBy);
    const query = params.toString();
    const response = await fetch(
      `${API_DL_URL}/v1/forge/admin/dashboards/data-collection${query ? `?${query}` : ''}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching data collection dashboard');
    }
    return response.json();
  }

  static async createDataCollectionRequest(
    token: string,
    request: DataCollectionCreateRequest
  ): Promise<DataCollectionCreateResponse> {
    const response = await fetch(`${API_DL_URL}/v1/forge/admin/data-collection/requests`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error creating data collection request');
    }
    return response.json();
  }

  static async completeDataCollectionRequest(
    token: string,
    requestId: string
  ): Promise<DataCollectionRequestRow> {
    const response = await fetch(
      `${API_DL_URL}/v1/forge/admin/data-collection/requests/${encodeURIComponent(requestId)}/complete`,
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error completing data collection request');
    }
    return response.json();
  }

  static async completeDataCollectionRequestsBatch(
    token: string,
    requestIds: string[]
  ): Promise<DataCollectionCompleteBatchResponse> {
    const response = await fetch(
      `${API_DL_URL}/v1/forge/admin/data-collection/requests/complete-batch`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ request_ids: requestIds }),
      }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error completing data collection requests');
    }
    return response.json();
  }

  static async fetchMasterLabelUnresolved(
    token: string
  ): Promise<MasterLabelUnresolvedDashboardResponse> {
    const existingRequest = inFlightMasterLabelUnresolvedRequests.get(token);
    if (existingRequest) {
      return existingRequest;
    }

    const request = (async (): Promise<MasterLabelUnresolvedDashboardResponse> => {
      const response = await fetch(
        `${API_DL_URL}/v1/forge/admin/dashboards/master-label-unresolved`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      ApiService.handleError(response);
      if (!response.ok) {
        throw await parseApiError(response, 'Error fetching Master Label unresolved list');
      }
      return response.json();
    })();

    inFlightMasterLabelUnresolvedRequests.set(token, request);
    try {
      return await request;
    } finally {
      inFlightMasterLabelUnresolvedRequests.delete(token);
    }
  }

  static async refreshMasterLabelUnresolved(
    token: string,
    chainId: number
  ): Promise<MasterLabelUnresolvedChainSnapshot> {
    const response = await fetch(
      `${API_DL_URL}/v1/forge/admin/dashboards/master-label-unresolved/refresh`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          chain_id: chainId,
        }),
      }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error refreshing Master Label unresolved snapshot');
    }
    return response.json();
  }

  static async fetchLiveStatus(token: string, chainId: number): Promise<LiveStatusSnapshot> {
    const requestKey = `${token}:${chainId}`;
    const existingRequest = inFlightLiveStatusRequests.get(requestKey);
    if (existingRequest) {
      return existingRequest;
    }

    const params = new URLSearchParams({
      chain_id: String(chainId),
    });
    const request = (async (): Promise<LiveStatusSnapshot> => {
      const response = await fetch(
        `${API_DL_URL}/v1/forge/admin/dashboards/live-status?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      ApiService.handleError(response);
      if (!response.ok) {
        throw await parseApiError(response, 'Error fetching live status snapshot');
      }
      return response.json();
    })();

    inFlightLiveStatusRequests.set(requestKey, request);
    try {
      return await request;
    } finally {
      inFlightLiveStatusRequests.delete(requestKey);
    }
  }

  static async refreshLiveStatus(token: string, chainId: number): Promise<LiveStatusSnapshot> {
    const response = await fetch(`${API_DL_URL}/v1/forge/admin/dashboards/live-status/refresh`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        chain_id: chainId,
      }),
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error refreshing live status snapshot');
    }
    return response.json();
  }

  static async fetchDailyReport(
    token: string,
    chainId: number,
    date: string
  ): Promise<DailyReportSnapshot> {
    const requestKey = `${token}:${chainId}:${date}`;
    const existingRequest = inFlightDailyReportRequests.get(requestKey);
    if (existingRequest) {
      return existingRequest;
    }

    const params = new URLSearchParams({
      chain_id: String(chainId),
      date,
    });
    const request = (async (): Promise<DailyReportSnapshot> => {
      const response = await fetch(
        `${API_DL_URL}/v1/forge/admin/dashboards/daily-report?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      ApiService.handleError(response);
      if (!response.ok) {
        throw await parseApiError(response, 'Error fetching daily report snapshot');
      }
      return response.json();
    })();

    inFlightDailyReportRequests.set(requestKey, request);
    try {
      return await request;
    } finally {
      inFlightDailyReportRequests.delete(requestKey);
    }
  }

  static async refreshDailyReport(
    token: string,
    chainId: number,
    date: string
  ): Promise<DailyReportSnapshot> {
    const response = await fetch(`${API_DL_URL}/v1/forge/admin/dashboards/daily-report/refresh`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        chain_id: chainId,
        date,
      }),
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error refreshing daily report snapshot');
    }
    return response.json();
  }

  static async fetchAggregateReport(
    token: string,
    chainId: number,
    dateFrom: string,
    dateTo: string
  ): Promise<DailyReportAggregateSnapshot> {
    const params = new URLSearchParams({
      chain_id: String(chainId),
      date_from: dateFrom,
      date_to: dateTo,
    });
    const response = await fetch(
      `${API_DL_URL}/v1/forge/admin/dashboards/aggregate-report?${params.toString()}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching aggregate report snapshot');
    }
    return response.json();
  }

  static async fetchItem(
    token: string,
    itemNumber: string,
    chainId: number
  ): Promise<ApiMasterLabelUPCData> {
    const params = new URLSearchParams({
      chain_id: String(chainId),
      include_inactive: 'true',
    });
    const response = await fetch(
      `${API_DL_URL}/v1/forge/admin/master-label/item-number/${encodeURIComponent(itemNumber)}?${params}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching item data');
    }
    return response.json();
  }

  static async fetchItemByBarcode(
    token: string,
    barcodeQuery: string,
    chainId: number
  ): Promise<ApiMasterLabelBarcodeLookupResponse> {
    const params = new URLSearchParams({
      chain_id: String(chainId),
      include_inactive: 'true',
    });
    const response = await fetch(
      `${API_DL_URL}/v1/forge/admin/master-label/barcode/${encodeURIComponent(barcodeQuery)}?${params}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching item data by barcode');
    }
    return response.json();
  }

  static async fetchItemByDesignId(
    token: string,
    designId: string,
    chainId: number
  ): Promise<ApiMasterLabelDesignLookupResponse> {
    const params = new URLSearchParams({
      chain_id: String(chainId),
      include_inactive: 'true',
    });
    const response = await fetch(
      `${API_DL_URL}/v1/forge/admin/master-label/design/${encodeURIComponent(designId)}?${params}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error fetching item data by design id');
    }
    return response.json();
  }

  static async saveState(
    token: string,
    request: MasterLabelStateSaveRequest
  ): Promise<MasterLabelStateSaveResponse> {
    const response = await fetch(`${API_DL_URL}/v1/forge/admin/master-label/state`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error saving Master Label state');
    }
    return response.json();
  }

  static async searchDesignGroups(
    token: string,
    chainId: number,
    query: string,
    page = 1,
    limit = 20
  ): Promise<ForgeDesignGroupSearchResponse> {
    const params = new URLSearchParams({
      chain_id: String(chainId),
      query,
      page: String(page),
      limit: String(limit),
    });
    const response = await fetch(`${API_DL_URL}/v1/forge/admin/design-groups/search?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error searching Forge design groups');
    }
    return response.json();
  }

  static async modifyDesignGroups(
    token: string,
    request: ModifyForgeDesignGroupsRequest
  ): Promise<ModifyForgeDesignGroupsResponse> {
    const response = await fetch(`${API_DL_URL}/v1/forge/admin/design-groups`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });
    ApiService.handleError(response);
    if (!response.ok) {
      throw await parseApiError(response, 'Error updating Forge design groups');
    }
    return response.json();
  }
}
